import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useAppContext } from '@/context';
import { useLogin } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { SignupDataType, getTokenFromStorage } from '@/utils/auth';
import { fixUrlForCloudFront } from '@/utils/cloudfront';
import { safeNavigate } from '@/utils/navigation';
import { TranslatedLink } from '@/utils/translatedComponents';

import Progress from './components/Progress';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import styles from './Signup.module.css';
import Logo from '../../../public/beatly.svg';

export function Signup() {
  const router = useRouter();
  const { setAlert } = useAppContext();
  const [inProgress, setInProgress] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SignupDataType>({
    account_type: '',
    first_name: '',
    email: ''
  });

  const { t } = useTranslation();

  function handleNext() {
    setCurrentStep(currentStep + 1);
  }

  const { mutate } = useLogin({
    ...formData,
    onSuccess: ({ code }: ResponseObject) => {
      if (code === 'INVALID_EMAIL') {
        setAlert('INVALID_EMAIL');
        setInProgress(false);
        return;
      }

      if (code === 'SENT_LOGIN_MAGICLINK' || code === 'SENT_REGISTER_MAGICLINK') {
        if (linkSent) {
          setAlert('EMAIL_WAS_RESENT');
        } else {
          setInProgress(false);
          setLinkSent(true);
          if (code === 'SENT_LOGIN_MAGICLINK') {
            setIsUserExist(true);
          }
          handleNext();
        }
        return;
      }
      setAlert('INTERNAL_SERVER_ERROR');
      setInProgress(false);
    }
  });

  useEffect(() => {
    const token = getTokenFromStorage();
    if (token) {
      // Use safeNavigate to avoid hard navigation conflicts
      safeNavigate(router, fixUrlForCloudFront('/connect'));
    }
  }, [router]);

  function handleChange(data: SignupDataType) {
    setFormData(data);
  }

  function handleSubmit() {
    setInProgress(true);
    mutate();
  }

  function resendData() {
    mutate();
  }

  return (
    <main className={styles.signup}>
      <TranslatedLink href="/login">
        <Logo className={styles.signupLogo} />
      </TranslatedLink>

      <div className={styles.signupForm}>
        <div className={styles.signupFormHeader}>
          <h2 className={styles.signupHeaderTitle}>{t('signup.title')}</h2>
          <div className={styles.signupHeaderDescription}>
            {!formData.account_type && t('signup.subtitle')}
            {formData.account_type === 'advertiser' && t('signup.subtitle.advertiser')}
            {formData.account_type === 'creator' && t('signup.subtitle.influencer')}
          </div>
          <Progress step={currentStep} />
        </div>

        {currentStep === 1 && <Step1 data={formData} onNext={handleNext} onChange={handleChange} />}

        {currentStep === 2 && (
          <Step2
            data={formData}
            inProgress={inProgress}
            onSubmit={handleSubmit}
            onChange={handleChange}
          />
        )}

        {currentStep === 3 && (
          <Step3 email={formData.email} userExist={isUserExist} onResend={resendData} />
        )}
      </div>
    </main>
  );
}
